import { Navigate, Route, Routes } from 'react-router-dom';
import React, { ReactElement, useEffect } from 'react';
import styles from './federated-app.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { addAuth0Context } from 'services/Service';
import { CircularProgress } from 'drift-ui';
import TermsAndConditions from '../pages/landing/TermsAndConditions';
import W9Certification from '../pages/landing/W9Certification';
import { RootState } from 'app/configureStore';
import { useFormNetworkCalls } from 'utils/useFormNetworkCalls';
import { SET_LEGAL_TEXTS } from 'app/actionTypes';
import { Banking } from '../pages/landing/Banking';
import { Factoring } from '../pages/landing/Factoring';
import { BranchData } from 'app/types';
import { DO_APPLICATION_TYPE } from 'client-experience-toolbox';

const Router = ({ features, branchData }: { features: string[]; branchData?: BranchData }): ReactElement => {
  const dispatch = useDispatch();
  const { isAuthenticated, isLoading, logout, getAccessTokenSilently } = useAuth0();
  const { getLegalText } = useFormNetworkCalls(DO_APPLICATION_TYPE.FACTORING);
  const legalTexts = useSelector((state: RootState) => state?.applicationReducer?.legalTexts);
  const legalLanguage = useSelector((state: RootState) => state.applicationReducer.language);
  const localLanguage = localStorage.getItem('i18nextLng') || 'en';
  useEffect(() => {
    try {
      if (isAuthenticated) {
        addAuth0Context(getAccessTokenSilently, isAuthenticated, logout);
        dispatch({ type: 'SET_BRANCH_DATA', branchData });
      }
    } catch (error) {
      console.error(error);
    }
  }, [getAccessTokenSilently, isAuthenticated, logout]);

  useEffect(() => {
    dispatch({ type: 'SET_ACTIVE_FEATURES', activeFeatures: features });
  }, [features, dispatch]);

  useEffect(() => {
    if (!legalTexts || legalTexts.length === 0 || legalLanguage !== localLanguage) {
      const fetchLegalTexts = async () => {
        try {
          const texts = await getLegalText();
          dispatch({ type: SET_LEGAL_TEXTS, legalTexts: texts, language: localLanguage });
        } catch (error) {
          console.error('Error fetching legal texts:', error);
          dispatch({ type: SET_LEGAL_TEXTS, legalTexts: [], language: localLanguage });
        }
      };
      fetchLegalTexts();
    }
  }, [getLegalText, legalTexts, dispatch, legalLanguage, localLanguage]);

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <div className={styles.content}>
      <div className={styles.appContentContainer}>
        <Routes>
          <Route path="/terms_and_conditions" element={<TermsAndConditions />} />
          <Route path="/w9_certification" element={<W9Certification />} />
          <Route path="*" element={<Navigate to="/factoring" replace />} />
          <Route path="/banking/:id" element={<Banking />} />
          <Route path="/factoring/:id" element={<Factoring />} />
        </Routes>
      </div>
    </div>
  );
};

export default Router;
